/**
 * DropdownController
 * Manages dropdown positioning using portal pattern to escape stacking contexts
 * 
 * @class DropdownController
 * @example
 * <div data-dropdown-container>
 *   <button data-dropdown-trigger>Toggle</button>
 *   <div class="dropdown" data-dropdown-content>...</div>
 * </div>
 */
class DropdownController {
  /**
   * @param {HTMLElement} container - Element with data-dropdown-container
   */
  constructor(container) {
    this.container = container;
    this.trigger = container.querySelector('[data-dropdown-trigger]');
    this.content = container.querySelector('[data-dropdown-content]');
    this.portal = document.getElementById('dropdown-portal');
    this.isOpen = false;
    
    // Bind methods to preserve context
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    
    this.init();
  }
  
  init() {
    if (!this.trigger || !this.content || !this.portal) {
      console.warn('DropdownController: Missing required elements', {
        trigger: !!this.trigger,
        content: !!this.content,
        portal: !!this.portal
      });
      return;
    }
    
    // Move dropdown content to portal
    this.portal.appendChild(this.content);
    
    // Bind events
    this.trigger.addEventListener('click', this.handleTriggerClick);
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscape);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }
  
  /**
   * Toggle dropdown open/close
   * @param {Event} event - Click event
   */
  handleTriggerClick(event) {
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }
  
  /**
   * Open dropdown and position it
   */
  open() {
    this.isOpen = true;
    this.position();
    this.content.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }
  
  /**
   * Close dropdown
   */
  close() {
    this.isOpen = false;
    this.content.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }
  
  /**
   * Position dropdown relative to trigger
   * Uses viewport coordinates since dropdown is in portal
   */
  position() {
    if (!this.isOpen || !this.content) return;
    
    const triggerRect = this.trigger.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Position below trigger with 8px gap
    let top = triggerRect.bottom + 8;
    
    // Align right edge of dropdown with right edge of trigger
    let left = triggerRect.right - contentRect.width;
    
    // Ensure it stays on screen horizontally
    if (left < 8) {
      left = 8;
    }
    if (left + contentRect.width > viewportWidth - 8) {
      left = viewportWidth - contentRect.width - 8;
    }
    
    // If dropdown would go below viewport, position above trigger
    if (top + contentRect.height > viewportHeight - 8) {
      top = triggerRect.top - contentRect.height - 8;
      
      // If still doesn't fit, position at bottom of viewport
      if (top < 8) {
        top = viewportHeight - contentRect.height - 8;
      }
    }
    
    this.content.style.top = `${top}px`;
    this.content.style.left = `${left}px`;
  }
  
  /**
   * Close dropdown when clicking outside
   * @param {Event} event - Click event
   */
  handleOutsideClick(event) {
    if (!this.isOpen) return;
    
    if (!this.container.contains(event.target) && 
        !this.content.contains(event.target)) {
      this.close();
    }
  }
  
  /**
   * Close dropdown on Escape key
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger.focus(); // Return focus to trigger
    }
  }
  
  /**
   * Reposition on scroll
   */
  handleScroll() {
    if (this.isOpen) {
      this.position();
    }
  }
  
  /**
   * Reposition on resize
   */
  handleResize() {
    if (this.isOpen) {
      this.position();
    }
  }
  
  /**
   * Cleanup event listeners
   */
  destroy() {
    this.trigger?.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscape);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    
    // Close if open
    if (this.isOpen) {
      this.close();
    }
  }
}

export default DropdownController;
